import {
    isPublicationCardItemWithVideo,
    toLinkWithHint,
    useFeature,
} from '@news-mono/web-common'

import React from 'react'
import { BaseCardProps } from '../Card'
import { CardLinkWrapper, StyledHeadline } from './ArticleCard.styled'
import {
    CardBodyWrapper,
    Teaser,
    ReadMore,
    LargeCardMedia,
    CardContainer,
    Byline,
} from '../components'
import { getByline, getLatestLiveBlog, getTopicLink } from '../utils'
import {
    EditorialType,
    getEditorialType,
    getFundingType,
} from '../../../templates'
import { TNLiveComponent } from '../../../content'
import { FundingLabel } from '../../../advertising/FundingLabel/FundingLabel'
import { BylineFundingTypeWrapper } from '../../../publication/Byline/components/TheNightly/Byline.styled'
import { PublishedTime } from '../components/card-body/PublishedTime'
import { StyledReadMoreWrapper } from '../components/card-body/ReadMore.styled'
import { isLiveEvent } from '../../../__helpers/is-live-event'
import { LatestLiveBlog } from '../components/card-body/LatestLiveBlog'

export interface ArticleCardProps extends BaseCardProps<'article-card'> {
    isStandalonePage?: boolean
    isStandaloneCard?: boolean
}

export const ArticleCard = ({
    item,
    onEvent,
    isStandalonePage,
    adTargeting,
    disableImageLazyLoad,
}: Omit<ArticleCardProps, 'kind'>) => {
    const eventLiveBlogTextLength = 250
    const classificationEnabled = true

    const { loaded: isLoaded } = item
    const result = isLoaded ? item.result : undefined
    const articleLink = toLinkWithHint(item)
    const byline = getByline(item)
    const topicLink = getTopicLink(item)
    const editorialType = isLoaded
        ? getEditorialType(item.result, classificationEnabled)
        : undefined
    const fundingType = isLoaded
        ? getFundingType(item.result, classificationEnabled)
        : undefined
    const showNormalClassification = !!editorialType
    const inlineVideoEnabled = useFeature('tn-inline-video-enabled')
    const willPlayVideoInline =
        isLoaded && inlineVideoEnabled
            ? isPublicationCardItemWithVideo(item.result)
            : false
    const showUpdated = isLiveEvent(result?.publicationKind)

    const isDisplayEventLatestPostEnabled = useFeature(
        'display-event-latest-post',
    )

    const latestLiveBlog =
        isDisplayEventLatestPostEnabled &&
        result &&
        result.publicationKind === 'event' &&
        getLatestLiveBlog(result.entries, eventLiveBlogTextLength)

    return (
        <CardContainer>
            {willPlayVideoInline ? (
                <LargeCardMedia
                    item={item}
                    onEvent={onEvent}
                    willPlayVideoInline={willPlayVideoInline}
                    adUnitPath={adTargeting?.adUnitPath}
                    disableImageLazyLoad={disableImageLazyLoad}
                />
            ) : (
                <CardLinkWrapper to={articleLink}>
                    {editorialType && !showNormalClassification && (
                        <TNLiveComponent
                            overlayOnImage={true}
                            showBackground={true}
                        />
                    )}
                    <LargeCardMedia
                        item={item}
                        onEvent={onEvent}
                        willPlayVideoInline={willPlayVideoInline}
                        adUnitPath={adTargeting?.adUnitPath}
                        disableImageLazyLoad={disableImageLazyLoad}
                    />
                </CardLinkWrapper>
            )}

            {result && (
                <CardBodyWrapper>
                    <BylineFundingTypeWrapper>
                        <Byline
                            editorialType={editorialType}
                            topicLink={topicLink}
                            topicTitle={result.primaryTopic.title}
                            bylineText={byline.text}
                            bylineLink={byline.link}
                            allowCommenting={result.allowCommenting}
                            articleUrl={result._self}
                        />
                        {fundingType && (
                            <FundingLabel
                                fundingType={fundingType}
                                linkToDisclaimer={true}
                            >
                                {fundingType}
                            </FundingLabel>
                        )}
                    </BylineFundingTypeWrapper>
                    <CardLinkWrapper to={articleLink}>
                        <StyledHeadline
                            as={isStandalonePage ? 'h2' : undefined}
                        >
                            {result.shortHeadline}
                        </StyledHeadline>
                        {latestLiveBlog ? (
                            <LatestLiveBlog
                                title={latestLiveBlog.title!}
                                content={latestLiveBlog.text}
                                publishedDate={latestLiveBlog.publishedDate}
                                showAsLive={
                                    editorialType &&
                                    editorialType === EditorialType.Live
                                }
                            />
                        ) : (
                            <Teaser>{result.teaser}</Teaser>
                        )}
                        <StyledReadMoreWrapper>
                            <PublishedTime
                                isLiveEvent={showUpdated}
                                publicationDate={
                                    showUpdated
                                        ? result.lastUpdated
                                        : result.publicationDate
                                }
                            />
                            {latestLiveBlog ? (
                                <ReadMore
                                    readTime={null}
                                    postCount={result.entries?.total}
                                />
                            ) : (
                                <ReadMore readTime={result.readTimeMinutes} />
                            )}
                        </StyledReadMoreWrapper>
                    </CardLinkWrapper>
                </CardBodyWrapper>
            )}
        </CardContainer>
    )
}
